<script lang="ts" setup>
import { computed } from 'vue'
import { PhArrowRight, PhGlobe, PhPlay } from '@phosphor-icons/vue'

import { InstagramIcon, FacebookIcon, TwitterIcon, TiktokIcon, SpotifyIcon, YoutubeIcon, VimeoIcon } from '../icons'
import { Button } from '../button'

import { PrivacyShieldContent, PrivacyShieldText, ImagePlaceholder, ImagePlaceholderContent, ImagePlaceholderTop, ImagePlaceholderBottom, ImagePlaceholderGraphic } from '.'

const props = defineProps<{
  embedType?: 'instagram' | 'facebook' | 'twitter' | 'tiktok' | 'spotify' | 'youtube' | 'vimeo' | 'iframe' | 'video' | 'embed'
  simulateContentPassOrContentPass?: boolean
  headline?: string
  description?: string | string[]
  consentButtonText?: string
  privacyPolicyButtonText?: string,
  newLayout?: boolean
}>()

const emit = defineEmits(['consentClick'])

const socialIcon = computed(() => {
  if (!props.embedType) return null

  const icons = {
    // Social media icons
    instagram: InstagramIcon,
    facebook: FacebookIcon,
    twitter: TwitterIcon,
    tiktok: TiktokIcon,
    spotify: SpotifyIcon,
    youtube: YoutubeIcon,
    vimeo: VimeoIcon,
    // Other embed types
    iframe: PhGlobe,
    video: PhPlay
  }
  return icons[props.embedType as keyof typeof icons]
})

</script>

<template>
  <div class="bg-aside flex flex-col gap-3 my-6 w-[389px]">
    <PrivacyShieldContent>
      <ImagePlaceholder>
        <ImagePlaceholderContent>
          <ImagePlaceholderTop>
            <ImagePlaceholderGraphic />
            <component :is="socialIcon"
                       v-if="socialIcon"
                       class="w-6 h-6 text-neutral-800" />
          </ImagePlaceholderTop>
          <ImagePlaceholderBottom>
            <component :is="socialIcon"
                       v-if="socialIcon"
                       class="w-12 h-12 text-neutral-800" />
          </ImagePlaceholderBottom>
        </ImagePlaceholderContent>
      </ImagePlaceholder>
      <PrivacyShieldText variant="headline">
        {{ headline }}
      </PrivacyShieldText>

      <template v-if="newLayout && !simulateContentPassOrContentPass">
        <PrivacyShieldText variant="regular">
          <span v-html="description?.[0] ?? ''" />
        </PrivacyShieldText>

        <PrivacyShieldText variant="regular">
          <span v-html="description?.[1] ?? ''" />
        </PrivacyShieldText>

        <PrivacyShieldText variant="link"
                           href="/datenschutz">
          <span v-html="description?.[2] ?? ''" />
        </PrivacyShieldText>

        <PrivacyShieldText variant="regular">
          <span v-html="description?.[3] ?? ''" />
        </PrivacyShieldText>

        <Button variant="primary"
                size="md"
                @click="emit('consentClick')">
          {{ consentButtonText }}
          <PhArrowRight weight="bold"
                        class="w-4 h-4" />
        </Button>
      </template>

      <template v-else>
        <template v-if="typeof description === 'string'">
          <PrivacyShieldText variant="regular">
            {{ description }}
          </PrivacyShieldText>
        </template>
        <template v-else-if="description">
          <PrivacyShieldText v-for="(text, index) in description"
                             :key="index"
                             variant="regular">
            {{ text }}
          </PrivacyShieldText>
        </template>
        <template v-if="!simulateContentPassOrContentPass">
          <Button variant="primary"
                  size="md"
                  @click="emit('consentClick')">
            {{ consentButtonText }}
            <PhArrowRight weight="bold"
                          class="w-4 h-4" />
          </Button>
          <Button variant="link"
                  size="md"
                  href="/datenschutz">
            {{ privacyPolicyButtonText }}
          </Button>
        </template>
      </template>
    </PrivacyShieldContent>
  </div>
</template>
